// Every admin BFF call goes through here. In production the shell loads Clerk (main.tsx), which
// keeps the session alive and hands us its short-lived (~60 s) session token; we send it as
// `Authorization: Bearer`. Without Clerk (preview, or its script failed to load) there is no
// getter and the request carries only the session cookie, as before.

export type TokenGetter = (opts?: { skipCache?: boolean }) => Promise<string | null>;

let tokenGetter: TokenGetter | null = null;

/** main.tsx registers Clerk's getToken once Clerk has loaded; null clears it. */
export function setTokenGetter(getter: TokenGetter | null): void {
  tokenGetter = getter;
}

async function withToken(init: RequestInit, skipCache: boolean): Promise<RequestInit> {
  if (!tokenGetter) return init;
  const token = await tokenGetter(skipCache ? { skipCache: true } : undefined).catch(() => null);
  if (!token) return init;
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
}

/**
 * fetch with the session token. A 401 means the token we sent had already expired (a laptop
 * waking from sleep, a throttled background tab): fetch a fresh one from Clerk and retry once.
 * Bodies here are strings or FormData, so the retry can send the same body again.
 */
export async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const r = await fetch(url, await withToken(init, false));
  if (r.status !== 401 || !tokenGetter) return r;
  return fetch(url, await withToken(init, true));
}
